import React, { useCallback, useEffect, useState } from "react";
import { MagnifyingGlass, Receipt, X } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { formatCurrency } from "shared/utils/currency";

/** Een kandidaat om aan te koppelen: een bestelbon of een offerte. */
export interface ParentDocumentOption {
  id: number;
  documentNumber: string;
  documentDate: string;
  isCreditNote?: boolean;
  supplier?: { name: string } | null;
  lines?: { totalExclVat?: number | string; totalInclVat?: number | string }[];
}

/**
 * Welk soort "ouder" er gekoppeld wordt. Bestelbon en offerte verschillen enkel in
 * hun endpoints en hun woorden; de kiezer zelf is voor beide dezelfde.
 */
export interface ParentDocumentKind {
  /** Achterste stuk van `GET /renovation/document/:id/<zoekpad>`. */
  zoekpad: "linkable-purchase-orders" | "linkable-quotes";
  /** Achterste stuk van `PUT /renovation/document/:id/<koppelpad>`. */
  koppelpad: "purchase-order" | "quote";
  /** Naam van het veld in de PUT-body. */
  bodyVeld: "purchaseOrderId" | "quoteId";
  /** "Bestelbon" / "Offerte" — met hoofdletter, voor titels. */
  enkelvoud: string;
  /** "bestelbonnen" / "offertes" — kleine letter, voor lopende tekst. */
  meervoud: string;
  /** Waarom er meerdere facturen aan één document mogen hangen. */
  toelichting: string;
  /** Placeholder van het zoekveld. */
  zoekPlaceholder: string;
  /** Tag voor Sentry, zodat de twee koppelingen apart te volgen zijn. */
  sentryTag: string;
}

export const BESTELBON_KOPPELING: ParentDocumentKind = {
  zoekpad: "linkable-purchase-orders",
  koppelpad: "purchase-order",
  bodyVeld: "purchaseOrderId",
  enkelvoud: "Bestelbon",
  meervoud: "bestelbonnen",
  toelichting:
    "Een bestelbon kan in schijven gefactureerd worden, dus meerdere facturen mogen aan dezelfde bestelbon hangen.",
  zoekPlaceholder: "Zoek op bestelbonnummer of leverancier...",
  sentryTag: "purchaseOrder",
};

export const OFFERTE_KOPPELING: ParentDocumentKind = {
  zoekpad: "linkable-quotes",
  koppelpad: "quote",
  bodyVeld: "quoteId",
  enkelvoud: "Offerte",
  meervoud: "offertes",
  toelichting:
    "Een goedgekeurde offerte wordt vaak in schijven gefactureerd, dus meerdere facturen mogen aan dezelfde offerte hangen. Op de offerte zie je daarna hoeveel er al gefactureerd is.",
  zoekPlaceholder: "Zoek op offertenummer of leverancier...",
  sentryTag: "quote",
};

interface Props {
  /** De factuur die gekoppeld wordt; `null` sluit de modal. */
  document: {
    id: number;
    documentNumber: string;
    /** Id van de al gekoppelde ouder, zodat die niet opnieuw gekozen kan worden. */
    parentId?: number | null;
    /** Nummer van de al gekoppelde ouder, voor de "Losmaken"-regel. */
    parentNumber?: string | null;
  } | null;
  kind: ParentDocumentKind;
  onClose: () => void;
  /** Aangeroepen na een geslaagde (ont)koppeling, zodat de lijst herlaadt. */
  onLinked: () => void;
}

const bedrag = (option: ParentDocumentOption) => {
  const totaal = (option.lines ?? []).reduce(
    (sum, line) =>
      sum + Math.abs(Number(line.totalInclVat ?? line.totalExclVat ?? 0) || 0),
    0,
  );
  return option.isCreditNote ? -totaal : totaal;
};

/**
 * Een factuur aan een **bestelbon** of aan een **offerte** koppelen.
 *
 * Beide koppelingen lopen van factuur naar het bovenliggende document: zo'n document
 * wordt vaak in schijven gefactureerd, dus één bestelbon/offerte kan meerdere facturen
 * hebben en een factuur hoort bij ten hoogste één van elk. Bij een offerte kán het ook
 * niet anders: er staat geen factuurnummer op, dus er valt niets automatisch te matchen.
 *
 * De kandidatenlijst komt **backend-side** en is beperkt tot documenten van hetzelfde
 * gebouw; zoeken gebeurt op nummer of leverancier, ook backend-side.
 */
export const LinkParentDocumentModal: React.FC<Props> = ({
  document,
  kind,
  onClose,
  onLinked,
}) => {
  const { session } = useSession();
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<ParentDocumentOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!document) setSearch("");
  }, [document]);

  useEffect(() => {
    if (!document || !session) return;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams();
        if (search.trim()) params.set("search", search.trim());
        const res = await fetchWithAuth(
          `${API_URL}/renovation/document/${document.id}/${kind.zoekpad}?${params}`,
          { method: "GET" },
          session,
        );
        if (!res.ok) throw new Error(`${kind.enkelvoud}en ophalen mislukt`);
        setOptions(await res.json());
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            module: "renovation-documents",
            action: `search-${kind.sentryTag}`,
          },
          extra: { documentId: document.id, search },
        });
        toast.error(`${kind.enkelvoud}s ophalen mislukt`);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [document, search, session, kind]);

  const save = useCallback(
    async (parentId: number | null) => {
      if (!document || !session) return;
      setIsSaving(true);
      try {
        const res = await fetchWithAuth(
          `${API_URL}/renovation/document/${document.id}/${kind.koppelpad}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ [kind.bodyVeld]: parentId }),
          },
          session,
        );
        if (!res.ok) {
          // De backend legt uit waarom (bv. het gekozen document is van het verkeerde type).
          const uitleg = await res.json().then((d) => d?.message).catch(() => undefined);
          throw new Error(uitleg || "Koppelen mislukt");
        }
        toast.success(
          parentId ? `${kind.enkelvoud} gekoppeld` : "Koppeling losgemaakt",
        );
        onLinked();
        onClose();
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            module: "renovation-documents",
            action: `link-${kind.sentryTag}`,
          },
          extra: { documentId: document.id, parentId },
        });
        toast.error((error as Error).message);
      } finally {
        setIsSaving(false);
      }
    },
    [document, session, onLinked, onClose, kind],
  );

  return (
    <Dialog open={!!document} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {kind.enkelvoud} koppelen aan {document?.documentNumber}
          </DialogTitle>
          <DialogDescription>
            {kind.toelichting} Enkel {kind.meervoud} van hetzelfde gebouw.
          </DialogDescription>
        </DialogHeader>

        {document?.parentNumber && (
          <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
            <span className="text-neutral-primary text-sm">
              Nu gekoppeld aan <strong>{document.parentNumber}</strong>
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={isSaving}
              onClick={() => save(null)}
              className="h-7 gap-1 text-xs"
            >
              <X size={12} />
              Losmaken
            </Button>
          </div>
        )}

        <div className="relative">
          <MagnifyingGlass
            size={14}
            className="text-neutral-secondary-mid absolute top-1/2 left-2.5 -translate-y-1/2"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={kind.zoekPlaceholder}
            className="h-8 pl-8 text-xs"
          />
        </div>

        <div className="max-h-72 space-y-1 overflow-y-auto">
          {isSearching ? (
            <div className="text-neutral-secondary-mid flex items-center gap-2 px-1 py-3 text-xs">
              <Spinner className="h-3 w-3" /> Zoeken...
            </div>
          ) : options.length === 0 ? (
            <p className="text-neutral-secondary-mid px-1 py-3 text-xs">
              Geen {kind.meervoud} gevonden voor dit gebouw.
            </p>
          ) : (
            options.map((option) => (
              <button
                key={option.id}
                onClick={() => save(option.id)}
                disabled={isSaving || option.id === document?.parentId}
                className="hover:bg-neutral-primary/5 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs disabled:opacity-50"
              >
                <Receipt size={13} weight="duotone" className="text-moss-primary shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-neutral-primary font-medium">
                    {option.documentNumber}
                  </span>
                  <span className="text-neutral-secondary-mid">
                    {" · "}
                    {option.supplier?.name ?? "geen leverancier"} ·{" "}
                    {option.documentDate
                      ? new Date(option.documentDate).toLocaleDateString("nl-BE")
                      : "—"}
                  </span>
                </span>
                <span className="text-neutral-primary shrink-0 font-medium">
                  {formatCurrency(bedrag(option))}
                </span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkParentDocumentModal;
